import { useTheme } from '@shopify/restyle';
import { Column, Row } from 'app/design/primitives/box';
import { Text } from 'app/design/primitives/text';
import { useActivitySectors, useToggleActivitySector } from 'app/network/groups';
import type { Theme } from 'app/shopify';
import { IconCheck } from '@tabler/icons-react-native';
import { useRef, useState } from 'react';
import { ActivityIndicator, Pressable } from 'react-native';
import { ICON_STROKE_WIDTH } from 'app/utils/constants';
import Toast from 'react-native-toast-message';
import { useTranslation } from 'react-i18next';

export function ActivitySectorChipsField() {
  const theme = useTheme<Theme>();
  const { t } = useTranslation('activity-sectors');

  const { data: sectors, isPending, isLoading } = useActivitySectors();
  const [pendingId, setPendingId] = useState<string | null>(null);
  const pendingIdRef = useRef<string | null>(null);
  const toggle = useToggleActivitySector({
    onError: () => {
      setPendingId(null);
      pendingIdRef.current = null;
      Toast.show({ type: 'error', position: 'bottom', bottomOffset: 100, text1: t('errorToast') });
    },
  });

  const selectedCount = (sectors ?? []).filter((s) => s.selected).length;

  const onToggle = (groupId: string, selected: boolean) => {
    if (pendingIdRef.current) return;
    if (selected && selectedCount <= 1) {
      Toast.show({ type: 'error', position: 'bottom', bottomOffset: 100, text1: t('minOneToast') });
      return;
    }
    pendingIdRef.current = groupId;
    setPendingId(groupId);
    toggle.mutate(
      { groupId, selected },
      {
        onSettled: () => {
          pendingIdRef.current = null;
          setPendingId((current) => (current === groupId ? null : current));
        },
      },
    );
  };

  const loading = isPending || isLoading;

  if (loading) {
    return (
      <Column gap="spacing.general.x1">
        <Text fontWeight="700" fontSize={14} color="text.primary">
          {t('fieldLabel')}
        </Text>
        <ActivityIndicator color={theme.colors['surface.brand.default']} size="small" />
      </Column>
    );
  }

  if (!sectors || sectors.length === 0) return null;

  return (
    <Column gap="spacing.general.x2">
      <Text fontWeight="700" fontSize={14} color="text.primary">
        {t('fieldLabel')}
      </Text>
      <Row flexWrap="wrap" rowGap="spacing.general.x2" columnGap="spacing.general.x2">
        {sectors.map((sector) => {
          const isSelected = sector.selected;
          const isPendingRow = pendingId === sector.id;
          return (
            <Pressable
              accessibilityRole="button"
              accessibilityState={{ selected: isSelected, busy: isPendingRow }}
              disabled={isPendingRow}
              key={sector.id}
              onPress={() => onToggle(sector.id, sector.selected)}
              style={({ pressed }) => ({
                flexDirection: 'row',
                alignItems: 'center',
                gap: theme.spacing['spacing.general.x1'],
                paddingVertical: theme.spacing['spacing.general.x1'],
                paddingHorizontal: theme.spacing['spacing.general.x2'],
                borderRadius: theme.borderRadii['radius.full'],
                backgroundColor: isSelected ? theme.colors['text.primary'] : theme.colors['surface.tertiary-neutral'],
                borderWidth: 1,
                borderColor: isSelected
                  ? theme.colors['text.primary']
                  : theme.colors['surface.inverse.tertiary-neutral'],
                opacity: pressed || isPendingRow ? 0.7 : 1,
              })}
            >
              {isSelected ? <IconCheck size={14} strokeWidth={ICON_STROKE_WIDTH} color="white" /> : null}
              <Text
                fontSize={13}
                fontWeight={isSelected ? '700' : '500'}
                style={isSelected ? { color: 'white' } : undefined}
              >
                {sector.name}
              </Text>
              {isPendingRow ? (
                <ActivityIndicator color={isSelected ? 'white' : theme.colors['text.primary']} size="small" />
              ) : null}
            </Pressable>
          );
        })}
      </Row>
    </Column>
  );
}
